import { attr, css, html, when } from "@microsoft/fast-element";
import { paddingNumber } from "../../styles/size";
import { foregroundOnPrimaryRest, getToken } from "../../design-tokens";
import { NatGenDesignSystem } from "../../natgen-design-system";
import { NatGenAnchor } from "../anchor/anchor";
import { display } from "../../foundation/display";

export type WizardStepState = 'done' | 'active' | 'unlocked' | 'locked';

// TODO: Add label orientation (top, right, etc) and recalculate tail as needed

/**
 * @element natgen-wizard-step
 */
export class NatGenWizardStep extends NatGenAnchor {
    @attr
    state: WizardStepState = 'locked';
}

/**
 * TODO: Change active state back to an outline after Viper agrees? States shouldn't have a check if they're not done.
:host([state=active]) .marker {
    outline: calc(${getToken('space-stroke-width')} * 2px) solid ${getToken('color-primary-base')};
    outline-offset: calc(${getToken('space-stroke-width')} * -2px)
}
 */
export const natGenWizardStepDefinition = NatGenWizardStep.compose({
    name: `${NatGenDesignSystem.prefix}-wizard-step`,
    styles: css`
        ${display('flex')}

        :host {
            --marker-size: var(--wizard-marker-size, calc(${paddingNumber} * 1px));
            flex: 1;
            flex-direction: column;
            text-align: center;
        }

        .marker {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            margin: auto;
            width: var(--marker-size);
            height: var(--marker-size);
            border-radius: 50%;
            color: ${foregroundOnPrimaryRest};
            background-color: ${getToken('color-neutral-base')};
            z-index: 1;
        }

        :host([state=done]) .marker, :host([state=unlocked]) .marker, :host([state=active]) .marker {
            background-color: ${getToken('color-primary-base')};
        }

        .container {
            position: relative;
        }

        .active-indicator {
            visibility: hidden;
            color: ${getToken('color-primary-base')};
        }

        :host([state=active]) .active-indicator {
            visibility: visible;
        }

        :host(:not(:first-child)) .tail {
            --tail-size: calc(${getToken('space-design-unit')} * 1px);
            position: absolute;
            left: -50%;
            top: calc(50% - var(--tail-size) / 2);
            width: calc(100%);
            height: var(--tail-size);
            background-color: ${getToken('color-neutral-base')};
        }

        :host([state=done]) .tail, :host([state=active]) .tail, :host([state=unlocked]) .tail {
            background-color: ${getToken('color-primary-base')};
        }
    `,
    template: html<NatGenWizardStep>`
        <template>
            <div class="active-indicator" part="active-indicator">
                <slot name="active-indicator">
                    <natgen-icon name="wizard-step-active-indicator" size="8"></natgen-icon>
                </slot>
            </div>

            <div class="container">
                <div class="marker" part="marker">
                    ${when(x => x.state == 'done' || x.state == 'active', html`
                        <slot name="icon">
                            <natgen-icon name="check" size="3"></natgen-icon>
                        </slot>
                    `)}
                </div>
                <div class="tail" part="tail"></div>
            </div>

            ${when(x => x.state == 'locked' || x.state == 'active', html<NatGenWizardStep>`
                <span><slot></slot></span>
            `, html<NatGenWizardStep>`
                <a
                    class="control"
                    part="control"
                    download="${x => x.download}"
                    href="${x => x.href}"
                    hreflang="${x => x.hreflang}"
                    ping="${x => x.ping}"
                    referrerpolicy="${x => x.referrerpolicy}"
                    rel="${x => x.rel}"
                    target="${x => x.target}"
                    type="${x => x.type}"
                    aria-atomic="${x => x.ariaAtomic}"
                    aria-busy="${x => x.ariaBusy}"
                    aria-controls="${x => x.ariaControls}"
                    aria-current="${x => x.ariaCurrent}"
                    aria-describedby="${x => x.ariaDescribedby}"
                    aria-details="${x => x.ariaDetails}"
                    aria-disabled="${x => x.ariaDisabled}"
                    aria-errormessage="${x => x.ariaErrormessage}"
                    aria-expanded="${x => x.ariaExpanded}"
                    aria-flowto="${x => x.ariaFlowto}"
                    aria-haspopup="${x => x.ariaHaspopup}"
                    aria-hidden="${x => x.ariaHidden}"
                    aria-invalid="${x => x.ariaInvalid}"
                    aria-keyshortcuts="${x => x.ariaKeyshortcuts}"
                    aria-label="${x => x.ariaLabel}"
                    aria-labelledby="${x => x.ariaLabelledby}"
                    aria-live="${x => x.ariaLive}"
                    aria-owns="${x => x.ariaOwns}"
                    aria-relevant="${x => x.ariaRelevant}"
                    aria-roledescription="${x => x.ariaRoledescription}"
                ><slot></slot></a>
            `)}
        </template>
    `
});
